import React from "react";

import { permissionSetFromSession, hasPermission } from "@auto/utils/permissions";
import { t } from "@auto/services";

import { MarkdownText } from "./MarkdownText";
import { ApiRequestConsole } from "./ApiRequestConsole";
import styles from "./ApiDocumentationPage.module.styl";

const pretty = (value) => JSON.stringify(value, null, 2);

const methodClass = (method) => {
  const value = String(method || "").toUpperCase();
  if (value === "GET") return styles.methodGet;
  if (value === "POST") return styles.methodPost;
  if (value === "PUT") return styles.methodPut;
  if (value === "PATCH") return styles.methodPatch;
  if (value === "DELETE") return styles.methodDelete;
  return styles.methodOther;
};

const shapeType = (shape) => {
  if (!shape) return "void";
  if (shape.type === "array") return "array";
  const base = shape.fieldType || shape.type || "object";
  if (shape.isArray || shape.isList) return `${base}[]`;
  return base;
};

const SchemaDetails = ({ shape, depth = 0 }) => {
  if (!shape) return null;

  const fields = Array.isArray(shape.fields) ? shape.fields : [];
  const values = shape.enum ?? shape.values;
  const defaultValue = shape.defaultValue ?? shape.default;

  return (
    <div className={depth ? styles.schemaNested : styles.schemaRoot}>
      <div className={styles.schemaTypeRow}>
        <code>{shapeType(shape)}</code>
        {shape.nullable === true ? <span className={styles.schemaFlag}>{t("developer.apiDocs.nullable")}</span> : null}
        {shape.unique === true ? <span className={styles.schemaFlag}>{t("developer.apiDocs.unique")}</span> : null}
        {shape.format ? <span className={styles.schemaFlag}>{shape.format}</span> : null}
      </div>

      {shape.description ? <div className={styles.schemaDescription}>{shape.description}</div> : null}
      {values !== undefined ? (
        <div className={styles.schemaMeta}><strong>{t("developer.apiDocs.values")}:</strong> <code>{pretty(values)}</code></div>
      ) : null}
      {defaultValue !== undefined ? (
        <div className={styles.schemaMeta}><strong>{t("developer.apiDocs.default")}:</strong> <code>{pretty(defaultValue)}</code></div>
      ) : null}

      {shape.type === "array" && shape.items ? (
        <div className={styles.schemaFields}>
          <div className={styles.schemaField}>
            <div className={styles.schemaFieldName}>{t("developer.apiDocs.item")}</div>
            <SchemaDetails shape={shape.items} depth={depth + 1} />
          </div>
        </div>
      ) : null}

      {fields.length ? (
        <div className={styles.schemaFields}>
          {fields.map((field, index) => (
            <div key={`${field.name || "field"}-${index}`} className={styles.schemaField}>
              <div className={styles.schemaFieldName}>{field.name}</div>
              <SchemaDetails shape={field} depth={depth + 1} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

const ParameterTable = ({ title, parameters }) => {
  if (!parameters?.length) return null;

  return (
    <div className={styles.contractBlock}>
      <h4>{title}</h4>
      <div className={styles.parameterTableWrap}>
        <table className={styles.parameterTable}>
          <thead>
            <tr>
              <th>{t("developer.apiDocs.name")}</th>
              <th>{t("developer.apiDocs.type")}</th>
              <th>{t("developer.apiDocs.description")}</th>
            </tr>
          </thead>
          <tbody>
            {parameters.map((parameter, index) => (
              <tr key={`${parameter.name}-${index}`}>
                <td><code>{parameter.name}</code></td>
                <td><code>{shapeType(parameter)}</code></td>
                <td>{parameter.description || ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StatusCodeTable = ({ statusCodes }) => {
  if (!Array.isArray(statusCodes) || !statusCodes.length) return null;

  return (
    <div className={styles.contractBlock}>
      <h4>{t("developer.apiDocs.statusCodes")}</h4>
      <div className={styles.parameterTableWrap}>
        <table className={styles.parameterTable}>
          <thead>
            <tr>
              <th>{t("developer.apiDocs.status")}</th>
              <th>{t("developer.apiDocs.meaning")}</th>
            </tr>
          </thead>
          <tbody>
            {statusCodes.map((item) => (
              <tr key={item.status}>
                <td><code>{item.status}</code></td>
                <td>{item.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const RuleList = ({ title, rules }) => {
  if (!Array.isArray(rules) || !rules.length) return null;

  return (
    <section className={styles.endpointSection}>
      <h2>{title}</h2>
      <ul className={styles.notesList}>
        {rules.map((rule, index) => <li key={index}>{rule}</li>)}
      </ul>
    </section>
  );
};

const executionAccess = (endpoint, session) => {
  const permissions = permissionSetFromSession(session);
  const policy = endpoint.executionPolicy;

  if (policy === "PUBLIC") {
    return { allowed: true, reason: "" };
  }

  if (policy === "AUTHENTICATED") {
    return { allowed: true, reason: "" };
  }

  if (policy === "PERMISSION") {
    if (!endpoint.permission) {
      throw new Error(`Endpoint ${endpoint.id} has PERMISSION policy without permission`);
    }
    return hasPermission(permissions, endpoint.permission)
      ? { allowed: true, reason: "" }
      : { allowed: false, reason: t("developer.apiDocs.requiresPermission", { permission: endpoint.permission }) };
  }

  if (policy === "PREDICATE") {
    return {
      allowed: false,
      reason: t("developer.apiDocs.predicateProtected"),
    };
  }

  if (policy === "UNSPECIFIED") {
    return {
      allowed: false,
      reason: t("developer.apiDocs.unspecifiedPolicy"),
    };
  }

  throw new Error(`Unknown execution policy ${policy} on endpoint ${endpoint.id}`);
};

const SecurityContract = ({ endpoint }) => {
  if (endpoint.executionPolicy === "PREDICATE") {
    return (
      <div className={styles.securityPill}>
        {t("developer.apiDocs.predicate")}
      </div>
    );
  }

  if (endpoint.permission) {
    return (
      <div className={styles.securityPill}>
        {t("developer.apiDocs.permission")} <code>{endpoint.permission}</code>
      </div>
    );
  }

  return (
    <div className={styles.securityPill}>
      {endpoint.executionPolicy}
    </div>
  );
};

export const ApiEndpointCard = ({ endpoint, documentation, session, onBack }) => {
  const access = executionAccess(endpoint, session);
  const request = endpoint.request || {};
  const response = endpoint.response || { type: "void" };

  return (
    <div className={styles.endpointPage}>
      <button type="button" className={styles.backButton} onClick={onBack}>
        ← {t("developer.apiDocs.documentationGuide")}
      </button>

      <div className={styles.endpointHeader}>
        <div className={styles.endpointIdentity}>
          <span className={`${styles.methodBadgeLarge} ${methodClass(endpoint.httpMethod)}`}>
            {endpoint.httpMethod}
          </span>
          <code className={styles.endpointPathLarge}>{endpoint.path}</code>
        </div>

        <h1>{documentation.title}</h1>
        <p className={styles.endpointSummary}>{documentation.summary}</p>

        <div className={styles.endpointMetaRow}>
          <span>{endpoint.sourceComponentName}</span>
          <span>›</span>
          <span>{endpoint.serviceName}</span>
          <SecurityContract endpoint={endpoint} />
        </div>
      </div>

      <section className={styles.endpointSection}>
        <h2>{t("developer.apiDocs.description")}</h2>
        <MarkdownText text={documentation.description} />
      </section>

      {documentation.usage ? (
        <section className={styles.endpointSection}>
          <h2>{t("developer.apiDocs.usage")}</h2>
          <MarkdownText text={documentation.usage} />
        </section>
      ) : null}

      <RuleList
        title={t("developer.apiDocs.authorizationRules")}
        rules={documentation.authorizationRules}
      />
      <RuleList
        title={t("developer.apiDocs.businessRules")}
        rules={documentation.businessRules}
      />

      <section className={styles.endpointSection}>
        <h2>{t("developer.apiDocs.request")}</h2>
        <ParameterTable title={t("developer.apiDocs.pathParameters")} parameters={request.pathParameters} />
        <ParameterTable title={t("developer.apiDocs.queryParameters")} parameters={request.queryParameters} />

        {request.body ? (
          <div className={styles.contractBlock}>
            <h4>{t("developer.apiDocs.body")}</h4>
            <SchemaDetails shape={request.body} />
          </div>
        ) : (
          <div className={styles.noBody}>{t("developer.apiDocs.noRequestBody")}</div>
        )}
      </section>

      <section className={styles.endpointSection}>
        <h2>{t("developer.apiDocs.response")}</h2>
        <SchemaDetails shape={response} />
        <StatusCodeTable statusCodes={documentation.statusCodes} />
      </section>

      {documentation.notes?.length ? (
        <section className={styles.endpointSection}>
          <h2>{t("developer.apiDocs.notes")}</h2>
          <ul className={styles.notesList}>
            {documentation.notes.map((note, index) => <li key={index}>{note}</li>)}
          </ul>
        </section>
      ) : null}

      <section className={styles.endpointSection}>
        <h2>{t("developer.apiDocs.tryIt")}</h2>
        <ApiRequestConsole endpoint={endpoint} enabled={access.allowed} disabledReason={access.reason} />
      </section>
    </div>
  );
};

export default ApiEndpointCard;
